/**
 * Storage provider context
 * Manages storage via backend HTTP API
 * Note: All chat history is stored in backend database via REST API
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  IStorageAdapter,
  StorageContextType,
} from '@/types/storage.types';
import { HttpStorageAdapter } from '@/services/storage/httpStorageAdapter';
import { reportError } from '@/services/errorTelemetry';

const StorageContext = createContext<StorageContextType | undefined>(
  undefined
);

export function useStorage(): StorageContextType {
  const context = useContext(StorageContext);
  if (!context) {
    throw new Error('useStorage must be used within StorageProvider');
  }
  return context;
}

interface StorageProviderProps {
  children: ReactNode;
  useTestAdapter?: boolean; // For tests only
}

export function StorageProvider({
  children,
  useTestAdapter = false,
}: StorageProviderProps) {
  // CRITICAL: All production chat history goes through backend HTTP API
  // Frontend never connects directly to databases
  // Backend handles all database operations internally

  const [adapter, setAdapter] = useState<IStorageAdapter | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize storage adapter
  useEffect(() => {
    let mounted = true;

    async function initializeAdapter() {
      try {
        setIsInitialized(false);
        setError(null);

        // Use SQLite adapter for tests, HttpStorageAdapter for production
        let newAdapter: IStorageAdapter;
        if (useTestAdapter || import.meta.env.MODE === 'test') {
          const { SQLiteAdapter } = await import(
            '@/services/storage/sqliteAdapter'
          );
          newAdapter = new SQLiteAdapter();
        } else {
          newAdapter = new HttpStorageAdapter();
        }

        console.log('[StorageContext] Initializing storage adapter...');
        await newAdapter.initialize();

        if (mounted) {
          setAdapter(newAdapter);
          setIsInitialized(true);
          console.log('[StorageContext] Storage adapter initialized successfully');
        }
      } catch (err) {
        reportError(err, { area: 'storage-init' }, 'error');
        console.error('[StorageContext] Failed to initialize storage adapter:', err);
        if (mounted) {
          setError((err as Error).message);

          // Fallback to SQLite if HTTP adapter fails
          if (!useTestAdapter && import.meta.env.MODE !== 'test') {
            console.warn('[StorageContext] Falling back to SQLite adapter');
            try {
              const { SQLiteAdapter } = await import(
                '@/services/storage/sqliteAdapter'
              );
              const fallbackAdapter = new SQLiteAdapter();
              await fallbackAdapter.initialize();
              setAdapter(fallbackAdapter);
              setIsInitialized(true);
            } catch (fallbackErr) {
              console.error('[StorageContext] Fallback adapter also failed:', fallbackErr);
            }
          }
        }
      }
    }

    initializeAdapter();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useTestAdapter]);

  const contextValue = useMemo<StorageContextType>(
    () => ({
      adapter: adapter!,
      isInitialized,
    }),
    [adapter, isInitialized],
  );

  if (!isInitialized || !adapter) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div
            className="mx-auto mb-4 h-12 w-12 animate-spin
            rounded-full border-4 border-stellar-primary
            border-t-transparent"
          />
          <p className="text-gray-600">Initializing storage...</p>
          {error && (
            <p className="mt-2 text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <StorageContext.Provider value={contextValue}>
      {children}
    </StorageContext.Provider>
  );
}
